// system/hooks/usePolling.ts
import { useRef, useCallback, useEffect } from 'react';

interface PollingOptions {
    maxAttempts?: number;
    maxErrorAttempts?: number;
    intervalMs?: number;
    timeoutMs?: number;
    errorMessages?: {  // ← добавили
        maxAttemptsError?: string;
        pollingTimeout?: string;
        taskNotFound?: string;
    };
}

export const usePolling = () => {
    const intervalsRef = useRef<NodeJS.Timeout[]>([]);
    const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            intervalsRef.current.forEach(interval => clearInterval(interval));
            timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
            intervalsRef.current = [];
            timeoutsRef.current = [];
        };
    }, []);

    const pollMultipleTasks = useCallback(async (
        taskIds: string[],
        getResult: (taskId: string) => Promise<any>,
        options: PollingOptions = {}
    ): Promise<any[]> => {
        const {
            maxAttempts = 60,
            maxErrorAttempts = 10,
            intervalMs = 5000,
            timeoutMs = 300000,
            errorMessages = {}
        } = options;

        const {
            maxAttemptsError = 'Превышено максимальное количество попыток',
            pollingTimeout = 'Превышено время ожидания',
            taskNotFound = 'Задача не найдена'
        } = errorMessages;

        const pollSingleTask = (taskId: string): Promise<any> => {
            return new Promise((resolve, reject) => {
                let attempts = 0;
                let errorAttempts = 0;
                let interval: NodeJS.Timeout;
                let globalTimeout: NodeJS.Timeout;

                const cleanup = () => {
                    if (interval) clearInterval(interval);
                    if (globalTimeout) clearTimeout(globalTimeout);
                };

                const poll = () => {
                    attempts++;

                    getResult(taskId)
                        .then(result => {
                            
                            if (result?.respType === 'isFailed') {
                                errorAttempts++;
                                
                                if (errorAttempts >= maxErrorAttempts) {
                                    cleanup();
                                    reject(result?.message || new Error(maxAttemptsError));
                                }
                                return;
                            }
                            
                            errorAttempts = 0;
                            
                            if (result?.gtinInfoList) {
                                cleanup();
                                resolve(result);
                                return;
                            }
                            
                            if (result?.status === 'Completed' || result?.state === 'Completed') {
                                cleanup();
                                resolve({ taskId, gtinInfoList: [] });
                                return;
                            }
                            
                            if (attempts >= maxAttempts) {
                                cleanup();
                                reject(new Error(maxAttemptsError));
                            }
                        })
                        .catch(error => {
                            errorAttempts++;
                            
                            if (error?.statusCode === 404) {
                                cleanup();
                                resolve({ taskId, gtinInfoList: [] });
                            } else if (errorAttempts >= maxErrorAttempts) {
                                cleanup();
                                reject(error?.message || new Error(maxAttemptsError));
                            }
                        });
                };

                globalTimeout = setTimeout(() => {
                    cleanup();
                    reject(new Error(pollingTimeout));
                }, timeoutMs);

                interval = setInterval(poll, intervalMs);
                poll();
            });
        };

        const promises = taskIds.map(taskId => pollSingleTask(taskId));
        const allResults = await Promise.all(promises);
        
        return allResults;
    }, []);

    return { pollMultipleTasks };
};